/**
 * R2 - Custom hook to fetch the landingPage singleton from Sanity
 * R3 - Includes loading/error states and fallback support
 * R4 - Single fetch on mount, cached via useCdn in the client
 */

import { useState, useEffect } from 'react';
import { sanityClient } from './client';
import { LANDING_PAGE_QUERY } from './queries';
import type { LandingPageData } from './types';

interface SanityDataState {
  data: LandingPageData | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches the entire landing page content in one GROQ query.
 * Components should use fallback values when data is null.
 *
 * @example
 * const { data, isLoading } = useSanityData();
 * const title = data?.hero?.headingPrefix ?? 'Creamos espacios';
 */
export function useSanityData(): SanityDataState {
  const [state, setState] = useState<SanityDataState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const controller = new AbortController();

    async function fetchData() {
      try {
        const result = await sanityClient.fetch<LandingPageData>(
          LANDING_PAGE_QUERY,
          {},
          { signal: controller.signal }
        );

        if (!controller.signal.aborted) {
          setState({ data: result, isLoading: false, error: null });
        }
      } catch (err: unknown) {
        if (controller.signal.aborted) return;

        const message =
          err instanceof Error ? err.message : 'Error fetching Sanity data';

        console.warn('[Sanity] Fetch failed, using fallback data:', message);
        setState({ data: null, isLoading: false, error: message });
      }
    }

    fetchData();

    // R4 - Cleanup: abort fetch if component unmounts
    return () => controller.abort();
  }, []);

  return state;
}
